import { createQuery, useMutation, useQueryClient } from '@tanstack/solid-query';
import { ArrowLeft, Check, CheckCheck, MessageSquare, Send, X } from 'lucide-solid';
import {
  For,
  type ParentComponent,
  Show,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  useContext,
} from 'solid-js';
import { useCan } from '~/shared/access/useCan';
import { useAuth } from '~/shared/api/auth-context';
import { type ChatMessageDto, type ConversationDto, chatApi } from '~/shared/api/chat-api';
import { ApiError } from '~/shared/api/http-client';
import { queryKeys } from '~/shared/api/query-keys';
import { settingsApi } from '~/shared/api/settings-api';
import { invokeHub, realtimeHub } from '~/shared/api/signalr';
import type { ChatMessagePayload } from '~/shared/realtime/realtime-events.gen';
import { SidePanel } from '~/shared/ui/SidePanel';
import { RoomsPane } from '~/features/chat/RoomsPane';
import { ChatCardBlock } from '~/features/chat/ChatCardBlock';
import { useToast } from '~/shared/ui/Toast';
import { clubDateOf, formatClubTime, formatInClubZone } from '~/shared/lib/clubZone';
import { useClubZone } from '~/shared/lib/useClubZone';

export interface ChatPanelContextValue {
  open: (conversationId?: string) => void;
  openForClient: (clientId: string) => Promise<void>;
  close: () => void;
}

const ChatPanelContext = createContext<ChatPanelContextValue>();

const QUICK_REPLIES = [
  'Здравствуйте! Чем можем помочь?',
  'Секунду, уточняю информацию.',
  'Подскажите, пожалуйста, номер брони.',
  'Готово! Проверьте, пожалуйста.',
  'Спасибо за обращение! Хорошего дня.',
];

const fmtTime = (iso: string | null, zone: string): string => {
  if (!iso || Number.isNaN(Date.parse(iso))) return '';
  const sameDay = clubDateOf(iso, zone) === clubDateOf(new Date().toISOString(), zone);
  return sameDay ? formatClubTime(iso, zone) : formatInClubZone(iso, zone, { day: 'numeric', month: 'short' });
};

interface Outgoing {
  conversationId: string;
  text: string;
  clientMessageId: string;
}

const uuid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

export const ChatPanelProvider: ParentComponent = (props) => {
  const { session } = useAuth();
  const zone = useClubZone();
  const toast = useToast();
  const qc = useQueryClient();

  const canRespond = useCan('/clients', 'chat');
  const myUserId = createMemo(() => session()?.user.id ?? null);

  const [isOpen, setOpen] = createSignal(false);
  const [activeId, setActiveId] = createSignal<string | null>(null);
  const [drafts, setDrafts] = createSignal<Record<string, string>>({});
  const draft = (): string => {
    const id = activeId();
    return id ? (drafts()[id] ?? '') : '';
  };
  const setDraft = (next: (current: string) => string): void => {
    const id = activeId();
    if (id) setDrafts((all) => ({ ...all, [id]: next(all[id] ?? '') }));
  };
  const [tab, setTab] = createSignal<'clients' | 'rooms'>('clients');

  const open = (conversationId?: string): void => {
    setTab('clients');
    setActiveId(conversationId ?? null);
    setOpen(true);
  };
  const close = (): void => {
    setOpen(false);
  };
  const backToInbox = (): void => {
    setActiveId(null);
  };

  const conversations = createQuery(() => ({
    meta: { peripheral: true },
    queryKey: queryKeys.chat.conversations(),
    queryFn: ({ signal }) => chatApi.listConversations({ signal }),
    enabled: isOpen() && canRespond(),
    staleTime: 10_000,
  }));

  const messages = createQuery(() => ({
    meta: { peripheral: true },
    queryKey: queryKeys.chat.messages(activeId() ?? 'none'),
    queryFn: ({ signal }) => chatApi.messages(activeId()!, 50, { signal }),
    enabled: isOpen() && canRespond() && !!activeId(),
  }));

  const activeConversation = createMemo<ConversationDto | undefined>(() =>
    conversations.data?.find((c) => c.id === activeId()),
  );
  const readOnly = createMemo(() => activeConversation()?.trainerId != null);

  const quickReplies = createQuery(() => ({
    meta: { peripheral: true },
    queryKey: ['settings', 'chat-quick-replies'],
    queryFn: ({ signal }) => settingsApi.getChatQuickReplies({ signal }),
    enabled: isOpen() && canRespond(),
    staleTime: 30_000,
  }));
  const replies = (): string[] =>
    quickReplies.data && quickReplies.data.length > 0 ? quickReplies.data : QUICK_REPLIES;

  const unsent = new Map<string, Outgoing>();

  const sendMut = useMutation(() => ({
    mutationFn: (out: Outgoing) => chatApi.send(out.conversationId, out.clientMessageId, out.text),
    onSuccess: (_message: ChatMessageDto, out: Outgoing) => {
      if (unsent.get(out.conversationId)?.clientMessageId === out.clientMessageId)
        unsent.delete(out.conversationId);
      setDrafts((all) => {
        if ((all[out.conversationId] ?? '').trim() !== out.text) return all;
        const { [out.conversationId]: _sent, ...rest } = all;
        return rest;
      });
      qc.invalidateQueries({ queryKey: queryKeys.chat.messages(out.conversationId) });
      qc.invalidateQueries({ queryKey: queryKeys.chat.conversations() });
    },
    onError: () => toast.error('Не удалось отправить сообщение'),
  }));

  const submit = (): void => {
    const conversationId = activeId();
    const text = draft().trim();
    if (!text || !conversationId || sendMut.isPending) return;
    const retry = unsent.get(conversationId);
    const clientMessageId = retry?.text === text ? retry.clientMessageId : uuid();
    const out: Outgoing = { conversationId, text, clientMessageId };
    unsent.set(conversationId, out);
    sendMut.mutate(out);
  };

  createEffect(() => {
    const id = activeId();
    const msgs = messages.data;
    if (!canRespond() || !id || !msgs || msgs.length === 0 || readOnly()) return;
    const maxSeq = msgs.reduce((mx, m) => (m.seq > mx ? m.seq : mx), 0);
    if (maxSeq > 0)
      void chatApi
        .markRead(id, maxSeq)
        .then(() => {
          qc.invalidateQueries({ queryKey: queryKeys.chat.conversations() });
        })
        .catch(() => {});
  });

  createEffect(() => {
    if (!canRespond()) return;
    const off = realtimeHub.on('ChatMessage', (p: ChatMessagePayload) => {
      if (p.senderIsStaff) return;
      const viewingThisThread = isOpen() && activeId() === p.conversationId;
      if (viewingThisThread) return;
      toast.info('Новое сообщение от клиента', {
        action: { label: 'Открыть', onClick: () => open(p.conversationId) },
      });
    });
    onCleanup(off);
  });

  const [theyTyping, setTheyTyping] = createSignal(false);
  let typingHideTimer: ReturnType<typeof setTimeout> | undefined;
  let lastTypingSent = 0;

  const onLocalType = (): void => {
    const now = Date.now();
    if (now - lastTypingSent < 2500) return;
    lastTypingSent = now;
    const id = activeId();
    const target = activeConversation()?.clientUserId;
    if (id && target && !readOnly()) invokeHub('ChatTyping', id, target);
  };

  createEffect(() => {
    activeId();
    setTheyTyping(false);
  });

  createEffect(() => {
    if (!canRespond()) return;
    const off = realtimeHub.on('ChatTyping', (p) => {
      if (p.fromStaff) return;
      if (p.conversationId !== activeId()) return;
      setTheyTyping(true);
      if (typingHideTimer) clearTimeout(typingHideTimer);
      typingHideTimer = setTimeout(() => setTheyTyping(false), 4000);
    });
    onCleanup(() => {
      off();
      if (typingHideTimer) clearTimeout(typingHideTimer);
    });
  });

  createEffect(() => {
    if (!canRespond()) return;
    const off = realtimeHub.on('ChatRead', (p) => {
      if (p.byStaff) return;
      qc.setQueryData<ConversationDto[]>(queryKeys.chat.conversations(), (old) =>
        old?.map((c) =>
          c.id === p.conversationId
            ?
              { ...c, clientLastReadSeq: Math.max(c.clientLastReadSeq ?? 0, p.uptoSeq) }
            : c,
        ),
      );
    });
    onCleanup(off);
  });

  const openForClient = async (clientId: string): Promise<void> => {
    setTab('clients');
    setOpen(true);
    try {
      const conversation = await chatApi.resolveForClient(clientId);
      setActiveId(conversation.id);
      void qc.invalidateQueries({ queryKey: queryKeys.chat.conversations() });
    } catch (err) {
      setActiveId(null);
      toast.error(
        err instanceof ApiError ? err.message : 'Не удалось открыть переписку с клиентом',
      );
    }
  };

  const value: ChatPanelContextValue = { open, openForClient, close };

  return (
    <ChatPanelContext.Provider value={value}>
      {props.children}

      <SidePanel
        open={isOpen()}
        onClose={close}
        ariaLabel="Поддержка — чат"
        bodyScroll={false}
        width="440px"
      >
        <div class="flex flex-col h-full">
          <header class="sticky top-0 z-10 flex items-center gap-2 px-4 h-14">
            <Show
              when={activeId()}
              fallback={
                <>
                  <MessageSquare class="h-[18px] w-[18px] text-accent" />
                  <span class="text-[15px] font-semibold flex-1">Поддержка</span>
                </>
              }
            >
              <button
                type="button"
                class="press h-8 w-8 -ml-1 flex items-center justify-center rounded-md hover:bg-white/6 text-secondary"
                aria-label="К списку диалогов"
                onClick={backToInbox}
              >
                <ArrowLeft class="h-[18px] w-[18px]" />
              </button>
              <div class="flex-1 min-w-0">
                <p class="text-[14px] font-semibold truncate">
                  {activeConversation()?.clientName ?? 'Клиент'}
                </p>
                <p class={`text-[11px] ${theyTyping() ? 'text-accent' : 'text-tertiary'}`}>
                  {theyTyping()
                    ? 'печатает…'
                    : readOnly()
                      ? `Тренер ${activeConversation()?.trainerName ?? ''} · только чтение`
                      : 'Диалог поддержки'}
                </p>
              </div>
            </Show>
            <button
              type="button"
              class="press h-8 w-8 flex items-center justify-center rounded-md hover:bg-white/6 text-secondary"
              aria-label="Закрыть"
              onClick={close}
            >
              <X class="h-[18px] w-[18px]" />
            </button>
          </header>

          <Show
            when={activeId()}
            fallback={
              <>
                <div class="flex gap-1 px-4 pb-2" role="tablist" aria-label="Чьи переписки">
                  <For each={[['clients', 'Клиенты'], ['rooms', 'События']] as const}>
                    {([key, label]) => (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={tab() === key}
                        class={`press text-[12px] px-3 py-1 rounded-full border border-hairline ${
                          tab() === key ? 'bg-white/10 text-primary' : 'text-secondary hover:bg-white/6'
                        }`}
                        onClick={() => setTab(key)}
                      >
                        {label}
                      </button>
                    )}
                  </For>
                </div>
                <Show when={tab() === 'rooms'}>
                  <RoomsPane zone={zone()} enabled={isOpen()} />
                </Show>
                <Show when={tab() === 'clients'}>
                  <InboxList
                    zone={zone()}
                    data={conversations.data}
                    loading={conversations.isLoading}
                    onOpen={(id) => setActiveId(id)}
                  />
                </Show>
              </>
            }
          >
            <Thread
              zone={zone()}
              data={messages.data}
              loading={messages.isLoading}
              myUserId={myUserId()}
              readSeq={activeConversation()?.clientLastReadSeq ?? 0}
            />

            <Show
              when={!readOnly()}
              fallback={
                <p class="border-t border-hairline p-3 text-[12px] text-tertiary text-center">
                  Переписку тренера с клиентом клуб только читает — ответить может тренер.
                </p>
              }
            >
              <div class="border-t border-hairline p-3 bg-white/2">
                <div class="flex flex-wrap gap-1.5 pb-2">
                  <For each={replies()}>
                    {(qr) => (
                      <button
                        type="button"
                        class="press text-[12px] px-2.5 py-1 rounded-full bg-white/6 hover:bg-white/10 text-secondary border border-hairline"
                        onClick={() => setDraft((d) => (d ? `${d} ${qr}` : qr))}
                      >
                        {qr}
                      </button>
                    )}
                  </For>
                </div>
                <div class="flex items-end gap-2">
                  <textarea
                    class="flex-1 resize-none max-h-32 min-h-[40px] rounded-md bg-white/5 border border-hairline px-3 py-2 text-[13px] text-primary placeholder:text-tertiary focus:outline-none focus:border-accent"
                    rows={1}
                    placeholder="Сообщение…"
                    value={draft()}
                    onInput={(e) => {
                      const value = e.currentTarget.value;
                      setDraft(() => value);
                      onLocalType();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        submit();
                      }
                    }}
                  />
                  <button
                    type="button"
                    class="press h-10 w-10 shrink-0 flex items-center justify-center rounded-md bg-accent text-white disabled:opacity-40"
                    aria-label="Отправить"
                    disabled={!draft().trim() || sendMut.isPending}
                    onClick={submit}
                  >
                    <Send class="h-[18px] w-[18px]" />
                  </button>
                </div>
              </div>
            </Show>
          </Show>
        </div>
      </SidePanel>
    </ChatPanelContext.Provider>
  );
};

const InboxList: ParentComponent<{
  zone: string;
  data: ConversationDto[] | undefined;
  loading: boolean;
  onOpen: (id: string) => void;
  emptyText?: string;
}> = (props) => (
  <div class="flex-1 overflow-y-auto">
    <Show
      when={!props.loading}
      fallback={<p class="text-[13px] text-tertiary p-6 text-center">Загрузка…</p>}
    >
      <Show
        when={(props.data?.length ?? 0) > 0}
        fallback={<p class="text-[13px] text-tertiary p-6 text-center">{props.emptyText ?? 'Пока нет диалогов'}</p>}
      >
        <For each={props.data}>
          {(c) => (
            <button
              type="button"
              class="press w-full text-left flex items-start gap-3 px-4 py-3 border-b border-hairline hover:bg-white/4"
              onClick={() => props.onOpen(c.id)}
            >
              <span class="mt-0.5 w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-teal to-accent flex items-center justify-center text-[12px] font-semibold text-white">
                {(c.clientName ?? '—').slice(0, 1).toUpperCase()}
              </span>
              <span class="flex-1 min-w-0">
                <span class="flex items-center gap-2">
                  <span class="text-[13px] font-semibold truncate flex-1">
                    {c.clientName ?? 'Клиент'}
                    <Show when={c.trainerName}>
                      <span class="font-normal text-tertiary"> · тренер {c.trainerName}</span>
                    </Show>
                  </span>
                  <span class="text-[11px] text-tertiary shrink-0">{fmtTime(c.lastMessageAt ?? null, props.zone)}</span>
                </span>
                <span class="flex items-center gap-2 mt-0.5">
                  <span class="text-[12px] text-tertiary truncate flex-1">
                    {c.lastMessagePreview ?? 'Нет сообщений'}
                  </span>
                  <Show when={c.unreadCount > 0}>
                    <span class="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-none flex items-center justify-center tabular">
                      {c.unreadCount > 9 ? '9+' : c.unreadCount}
                    </span>
                  </Show>
                </span>
              </span>
            </button>
          )}
        </For>
      </Show>
    </Show>
  </div>
);

const Thread: ParentComponent<{
  zone: string;
  data: ChatMessageDto[] | undefined;
  loading: boolean;
  myUserId: string | null;
  readSeq: number;
}> = (props) => {
  let scrollRef: HTMLDivElement | undefined;
  createEffect(() => {
    void props.data?.length;
    queueMicrotask(() => {
      if (scrollRef) scrollRef.scrollTop = scrollRef.scrollHeight;
    });
  });

  return (
    <div ref={scrollRef} class="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-2">
      <Show
        when={!props.loading}
        fallback={<p class="text-[13px] text-tertiary text-center py-6">Загрузка…</p>}
      >
        <Show
          when={(props.data?.length ?? 0) > 0}
          fallback={
            <p class="text-[13px] text-tertiary text-center py-6">
              Начните диалог первым сообщением
            </p>
          }
        >
          <For each={props.data}>
            {(m) => {
              const mine =
                m.senderIsStaff && props.myUserId != null && m.senderUserId === props.myUserId;
              const staff = m.senderIsStaff;
              return (
                <div
                  class={`flex flex-col max-w-[82%] ${staff ? 'self-end items-end' : 'self-start items-start'}`}
                >
                  <div
                    class={`px-3 py-2 rounded-2xl text-[13px] leading-snug whitespace-pre-wrap break-words ${
                      staff
                        ? 'bg-accent text-white rounded-br-sm'
                        : 'bg-white/8 text-primary rounded-bl-sm'
                    }`}
                  >
                    <Show when={m.card} fallback={m.body}>
                      {(card) => <ChatCardBlock card={card()} />}
                    </Show>
                  </div>
                  <span class="text-[10px] text-tertiary mt-1 px-1 flex items-center gap-1">
                    <span>
                      {staff
                        ? `${mine ? 'Вы' : m.senderUserId == null ? 'Клуб' : (m.senderName ?? 'Сотрудник')} · ${fmtTime(m.createdAt, props.zone)}`
                        : fmtTime(m.createdAt, props.zone)}
                    </span>
                    <Show when={staff}>
                      {m.seq <= props.readSeq ? (
                        <CheckCheck class="h-3 w-3 text-accent" />
                      ) : (
                        <Check class="h-3 w-3 text-tertiary" />
                      )}
                    </Show>
                  </span>
                </div>
              );
            }}
          </For>
        </Show>
      </Show>
    </div>
  );
};

export const useChatPanel = (): ChatPanelContextValue => {
  const ctx = useContext(ChatPanelContext);
  if (!ctx) throw new Error('useChatPanel must be used within <ChatPanelProvider>');
  return ctx;
};
